import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import moment from 'moment-timezone';

import { BehaviorSubject } from 'rxjs';

import { BridgeAPIService } from 'app/providers/bridge-api.service';
import { EnableClassTelemetry } from 'app/services/otel.service';

import { SystemService } from 'app/services/system.service';

import { WrappedRequest } from '../api.service';
import { Constants } from '../../constants/constants';
import { AuthService } from './auth.service';

export interface Preferences {
  id: string;
  timezone: string;
  timezoneOffset: string;
  homepage: string;
  lastConfig: string;
  projectSorting: number;
  langCode: string;
  fieldSorting: string;
  _: any;
}

export interface PreferencesResult {
  result: Preferences;
}

@EnableClassTelemetry()
@Injectable()
export class SemantriaPreferences extends BridgeAPIService {
  private preferencesSub$ = new BehaviorSubject(moment.tz.guess());
  private preferencesRequest: WrappedRequest<any[]>;
  private preferences: Preferences;

  static readonly DEFAULT_LANGUAGE_CODE = 'en';

  constructor(
    httpClient: HttpClient,
    systemService: SystemService,
    private authService: AuthService
  ) {
    super(httpClient, systemService);
  }

  public static mapPreferences(result: PreferencesResult): Preferences {
    if (!result.result.timezone) {
      result.result.timezone = moment.tz.guess();
    }
    const offsetMinutes: number = moment().tz(result.result.timezone).utcOffset();
    const hours = Math.floor(Math.abs(offsetMinutes) / 60);
    const minutes = Math.floor(Math.abs(offsetMinutes) % 60);
    let offset = ('' + hours).padStart(2, '0') + ':' + ('' + minutes).padStart(2, '0');
    if (offsetMinutes < 0) {
      offset = '-' + offset;
    } else {
      offset = '+' + offset;
    }
    result.result.timezoneOffset = offset;
    return result.result;
  }

  public async getPreferences(force: boolean = false): Promise<Preferences> {
    if (this.preferences && !force) {
      return this.preferences;
    }

    if (!this.preferencesRequest?.isInProgress()) {
      const prefPromise = Promise.all([
        this.get<PreferencesResult>('/users/preferences'),
        this.get<PreferencesResult>('/accounts/preferences'),
      ]);

      this.preferencesRequest = new WrappedRequest(prefPromise);
    }
    const prefRequest = await this.preferencesRequest.request;
    let userPreferences = prefRequest[0];
    const accountPreferences = prefRequest[1];

    if (userPreferences.result['_type'] === 'shared') {
      userPreferences = this.getSharedPreferences();
    }

    if (accountPreferences.result?.[Constants.SPOTLIGHT_DOWNLOAD_SHOW_WARNING]) {
      accountPreferences.result[Constants.SPOTLIGHT_DOWNLOAD_SHOW_WARNING] = JSON.parse(
        accountPreferences.result[Constants.SPOTLIGHT_DOWNLOAD_SHOW_WARNING]
      );
    }
    this.preferences = {
      ...accountPreferences.result,
      ...SemantriaPreferences.mapPreferences(userPreferences),
    };
    this.preferencesSub$.next(this.preferences.timezone);

    return this.preferences;
  }

  private getSharedPreferences(): PreferencesResult {
    const json = this.localStorage.getItem('sharedPrefrences');
    const prefs = json
      ? JSON.parse(json)
      : { result: { timezone: 'America/New_York', timezoneOffset: '-05:00' } };
    return prefs as PreferencesResult;
  }

  private saveSharedPreference(prefName: string, prefValue: any) {
    const prefs = this.getSharedPreferences();
    prefs.result[prefName] = prefValue;
    this.localStorage.setItem('sharedPrefrences', prefs, 'local', true);
  }

  public async savePreference(prefName: string, prefValue: any): Promise<Preferences> {
    if (this.authService.isSharedSession) {
      this.saveSharedPreference(prefName, prefValue);
      return this.getSharedPreferences().result;
    }

    const data = {
      prefName,
      prefValue,
    };
    const result = await this.put<PreferencesResult>('/users/preferences', data);
    this.preferences = {
      ...this.preferences,
      ...SemantriaPreferences.mapPreferences(result),
    };
    this.preferencesSub$.next(this.preferences.timezone);
    return this.preferences;
  }

  public async saveAccountPreference(prefName: string, prefValue: any): Promise<Preferences> {
    const data = {
      prefName,
      prefValue,
    };
    const result = await this.put<PreferencesResult>('/accounts/preferences', data);
    this.preferences = {
      ...this.preferences,
      ...SemantriaPreferences.mapPreferences(result),
    };
    return this.preferences;
  }

  public subscribePreferences(handler) {
    this.preferencesSub$.subscribe(handler);
  }

  get hasDownloadWarningShow(): boolean {
    return this.preferences?.[Constants.SPOTLIGHT_DOWNLOAD_SHOW_WARNING] === true;
  }
}
